<template>

  <div ref="rootRef" class="project-select-custom" :class="{ 'is-open': open }">

    <div ref="triggerRef" class="project-select-trigger" @click="onTriggerClick">

      <input

        v-if="open && filterable"

        ref="searchInputRef"

        v-model="search"

        class="project-select-search"

        type="text"

        :placeholder="placeholder"

        @click.stop

        @keydown.esc.prevent="close"

      />

      <span

        v-else

        class="project-select-trigger-text"

        :class="{ 'is-placeholder': !selectedTitle }"

        :title="selectedTitle"

      >

        {{ selectedTitle || placeholder }}

      </span>

      <i class="h-icon-down project-select-arrow"></i>

      <i

        v-if="modelValue && modelValue !== allProjectsLabel && !open"

        class="h-icon-close-min project-select-clear"

        title="清除"

        @click.stop="clear"

      ></i>

    </div>

    <Teleport to="body">

      <div

        v-show="open"

        ref="panelRef"

        class="project-select-panel"

        :style="panelStyle"

      >

        <div

          v-for="item in filteredOptions"

          :key="item.key"

          class="project-select-option"

          :class="{ 'is-active': item.key === modelValue }"

          @click="selectItem(item)"

        >

          <div class="project-option-title">{{ item.title }}</div>

          <div v-if="item.wbs" class="project-option-prefix">WBS：{{ item.wbs }}</div>

        </div>

        <div v-if="!filteredOptions.length" class="project-select-empty">无匹配项目</div>

      </div>

    </Teleport>

  </div>

</template>



<script setup>

import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';



const props = defineProps({

  modelValue: { type: String, default: '' },

  options: { type: Array, default: () => [] },

  placeholder: { type: String, default: '选择项目' },

  filterable: { type: Boolean, default: true },

  allProjectsLabel: { type: String, default: '全部项目' },

});



const emit = defineEmits(['update:modelValue', 'change']);



const rootRef = ref(null);

const triggerRef = ref(null);

const panelRef = ref(null);

const searchInputRef = ref(null);

const open = ref(false);

const search = ref('');

const panelStyle = ref({});



const selectedItem = computed(() => props.options.find((item) => item.key === props.modelValue));

const selectedTitle = computed(() => selectedItem.value?.title || '');



const filteredOptions = computed(() => {

  const keyword = search.value.trim().toLowerCase();

  if (!keyword) return props.options;

  return props.options.filter((item) => {

    const title = String(item.title || '').toLowerCase();

    const wbs = String(item.wbs || '').toLowerCase();

    return title.includes(keyword) || wbs.includes(keyword);

  });

});



function updatePanelPosition() {

  const trigger = triggerRef.value;

  if (!trigger) return;

  const rect = trigger.getBoundingClientRect();

  const viewportWidth = window.innerWidth;

  const panelWidth = Math.min(Math.max(rect.width, 720), viewportWidth - 16);

  let left = rect.left;

  if (left + panelWidth > viewportWidth - 8) {

    left = Math.max(8, viewportWidth - panelWidth - 8);

  }

  panelStyle.value = {

    position: 'fixed',

    top: `${rect.bottom + 4}px`,

    left: `${left}px`,

    width: `${panelWidth}px`,

    zIndex: 3000,

  };

}



function onTriggerClick() {

  if (open.value) return;

  open.value = true;

  search.value = '';

  nextTick(() => {

    updatePanelPosition();

    searchInputRef.value?.focus();

  });

}



function close() {

  open.value = false;

  search.value = '';

}



function selectItem(item) {

  emit('update:modelValue', item.key);

  emit('change', item.key);

  close();

}



function clear() {

  emit('update:modelValue', props.allProjectsLabel);

  emit('change', props.allProjectsLabel);

}



function onDocumentClick(event) {

  if (!open.value) return;

  const target = event.target;

  if (rootRef.value?.contains(target) || panelRef.value?.contains(target)) return;

  close();

}



function onViewportChange() {

  if (open.value) updatePanelPosition();

}



watch(

  () => props.modelValue,

  () => {

    if (!open.value) search.value = '';

  }

);



watch(open, (isOpen) => {

  if (isOpen) nextTick(updatePanelPosition);

});



onMounted(() => {

  document.addEventListener('click', onDocumentClick);

  window.addEventListener('resize', onViewportChange);

  window.addEventListener('scroll', onViewportChange, true);

});



onBeforeUnmount(() => {

  document.removeEventListener('click', onDocumentClick);

  window.removeEventListener('resize', onViewportChange);

  window.removeEventListener('scroll', onViewportChange, true);

});

</script>



<style lang="less" scoped>

@import '@/styles/variables.less';



.project-select-custom {

  position: relative;

  width: 100%;

}



.project-select-trigger {

  position: relative;

  display: flex;

  align-items: center;

  min-height: 36px;

  height: 36px;

  padding: 0 30px 0 10px;

  border: 1px solid #d8dee6;

  border-radius: @app-radius;

  background: #fff;

  cursor: pointer;

  transition: border-color 0.2s;



  .is-open & {

    border-color: @app-primary;

  }



  &:hover {

    border-color: #c5ced8;

  }

}



.project-select-trigger-text {

  flex: 1;

  min-width: 0;

  overflow: hidden;

  text-overflow: ellipsis;

  white-space: nowrap;

  font-size: 14px;

  color: @app-text;



  &.is-placeholder {

    color: #b0b8c4;

  }

}



.project-select-search {

  flex: 1;

  min-width: 0;

  height: 100%;

  border: none;

  outline: none;

  background: transparent;

  font-size: 14px;

  color: @app-text;

}



.project-select-arrow {

  position: absolute;

  right: 10px;

  top: 50%;

  margin-top: -7px;

  font-size: 14px;

  color: @app-muted;

  pointer-events: none;

}



.project-select-clear {

  position: absolute;

  right: 28px;

  top: 50%;

  margin-top: -7px;

  font-size: 12px;

  color: @app-muted;

  padding: 2px;

  border-radius: 50%;

  cursor: pointer;



  &:hover {

    color: @app-text;

    background: #f0f3f6;

  }

}

</style>



<style lang="less">

@import '@/styles/variables.less';



.project-select-panel {

  max-height: 380px;

  overflow-y: auto;

  overflow-x: visible;

  background: #fff;

  border: 1px solid #e8ecf1;

  border-radius: @app-radius;

  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);

  padding: 6px 0;

}



.project-select-option {

  padding: 10px 14px;

  cursor: pointer;

  transition: background 0.15s;



  &:hover {

    background: #f7f9fc;

  }



  &.is-active {

    background: @app-primary-light;



    .project-option-title {

      color: @app-primary;

    }

  }

}



.project-option-title {

  font-size: 13px;

  line-height: 1.55;

  color: @app-text;

  white-space: normal;

  word-break: break-word;

  overflow-wrap: anywhere;

}



.project-option-prefix {

  margin-top: 4px;

  font-size: 12px;

  line-height: 1.4;

  color: @app-muted;

  font-family: Consolas, 'Courier New', monospace;

  white-space: nowrap;

}



.project-select-empty {

  padding: 12px 14px;

  font-size: 13px;

  color: @app-muted;

  text-align: center;

}

</style>

